/**
 * HTTP Client Utility
 * Centralized place for all API calls
 * Provides configured Axios instance for API calls
 */

import axios from "axios";
import type { AxiosInstance, AxiosRequestConfig } from "axios";
import { deleteCookie } from "../helpers/cookieHelper";

// Singleton API client instance
let apiClientInstance: AxiosInstance | null = null;

/**
 * Creates and configures the Axios instance
 */
const createApiClient = (): AxiosInstance => {
  const baseURL = import.meta.env.VITE_API_BASE_URL;
  const api = axios.create({ 
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor for adding custom headers
  api.interceptors.request.use(
    (config) => {
      // Future: Add any custom headers here if needed
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      console.error("API Error:", error?.response?.data || error.message);
      
      // Handle specific error codes
      if (error.response?.status === 401) {
        // Handle unauthorized access - clear session and redirect to login
        console.warn("Unauthorized access - clearing session and redirecting to login");
        
        // Clear authentication cookie
        deleteCookie('qms_user');
        
        // Redirect to login page
        window.location.href = '/login';
      } else if (error.response?.status === 403) {
        console.warn("Forbidden access");
      }
      
      return Promise.reject(error);
    }
  );

  return api;
};

/**
 * Get the singleton API client instance
 */
export const getApiClient = (): AxiosInstance => {
  if (!apiClientInstance) {
    apiClientInstance = createApiClient();
  }
  return apiClientInstance;
};

/**
 * Centralized API call methods
 */
export const httpClient = {
  /**
   * GET request
   */
  get: <T = unknown>(url: string, config?: AxiosRequestConfig) => {
    return getApiClient().get<T>(url, config);
  },

  /**
   * POST request
   */
  post: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) => {
    return getApiClient().post<T>(url, data, config);
  },

  /**
   * PUT request
   */
  put: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) => {
    return getApiClient().put<T>(url, data, config);
  },

  /**
   * PATCH request
   */
  patch: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) => {
    return getApiClient().patch<T>(url, data, config);
  },

  /**
   * DELETE request
   */
  delete: <T = unknown>(url: string, config?: AxiosRequestConfig) => {
    return getApiClient().delete<T>(url, config);
  },
};

// Default export for backward compatibility
export default httpClient;
